import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { Dialog } from ".";
import { IoWarning } from "react-icons/io5";
import { useTranslation } from "react-i18next";

// Confirmation Dialog Types
interface ConfirmationOptions {
  title?: ReactNode;
  message?: ReactNode;
  confirmText?: ReactNode;
  cancelText?: ReactNode;
  variant?: "default" | "danger";
}

interface ConfirmationContextType {
  confirm: (options: ConfirmationOptions) => Promise<boolean>;
}

// Confirmation Context
const ConfirmationContext = createContext<ConfirmationContextType | null>(null);

// Confirmation Dialog Component
interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: ReactNode;
  message: ReactNode;
  confirmText: ReactNode;
  cancelText: ReactNode;
  variant: "default" | "danger";
}

function ConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  variant,
}: ConfirmationDialogProps) {
  const [dialogOpen, setDialogOpen] = useState(isOpen);

  useEffect(() => {
    setDialogOpen(isOpen);
  }, [isOpen]);

  const handleConfirm = () => {
    onConfirm();
    setDialogOpen(false);
  };

  const handleCancel = () => {
    onClose();
    setDialogOpen(false);
  };

  const getVariantStyles = () => {
    switch (variant) {
      case "danger":
        return {
          confirmButton:
            "bg-red-600 hover:bg-red-700 text-white dark:bg-red-700 dark:hover:bg-red-800 dark:text-white",
          icon: (
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <IoWarning className="text-red-500 dark:text-red-400" size={20} />
            </div>
          ),
        };
      default:
        return {
          confirmButton:
            "bg-blue-600 hover:bg-blue-700 text-white dark:bg-blue-700 dark:hover:bg-blue-800 dark:text-white",
          icon: (
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
              <svg
                className="h-6 w-6 text-blue-600 dark:text-blue-400"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z"
                />
              </svg>
            </div>
          ),
        };
    }
  };

  const styles = getVariantStyles();

  return (
    <Dialog
      isOpen={dialogOpen}
      setIsOpen={setDialogOpen}
      onClose={handleCancel}
      className="w-full max-w-md"
    >
      <div className="text-center">
        {styles.icon}

        <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-gray-100">
          {title}
        </h3>

        <div className="mt-2 text-lg text-gray-600 dark:text-gray-300">
          {message}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={handleCancel}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${styles.confirmButton}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

// Confirmation Provider
interface ConfirmationProviderProps {
  children: React.ReactNode;
}

export function ConfirmationProvider({ children }: ConfirmationProviderProps) {
  const { t } = useTranslation();
  const [confirmationState, setConfirmationState] = useState<{
    isOpen: boolean;
    options: ConfirmationOptions;
    resolve: (value: boolean) => void;
  } | null>(null);

  const confirm = (options: ConfirmationOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmationState({
        isOpen: true,
        options: {
          title: t("common.confirmAction"),
          message: t("common.areYouSureToProceed"),
          confirmText: t("common.confirm"),
          cancelText: t("common.cancel"),
          variant: "default",
          ...options,
        },
        resolve,
      });
    });
  };

  const handleConfirm = () => {
    if (confirmationState) {
      confirmationState.resolve(true);
      setConfirmationState(null);
    }
  };

  const handleCancel = () => {
    if (confirmationState) {
      confirmationState.resolve(false);
      setConfirmationState(null);
    }
  };

  return (
    <ConfirmationContext.Provider value={{ confirm }}>
      {children}
      {confirmationState && (
        <ConfirmationDialog
          isOpen={confirmationState.isOpen}
          onClose={handleCancel}
          onConfirm={handleConfirm}
          title={confirmationState.options.title!}
          message={confirmationState.options.message!}
          confirmText={confirmationState.options.confirmText!}
          cancelText={confirmationState.options.cancelText!}
          variant={confirmationState.options.variant!}
        />
      )}
    </ConfirmationContext.Provider>
  );
}
export function useConfirmation() {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error(
      "useConfirmation must be used within a ConfirmationProvider"
    );
  }
  return context;
}

export { ConfirmationDialog };
